import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { EventEntry, EventColumn, HighlightWindow } from '../types';
import { EVENT_LOG_MAX } from '../config/constants';
import { nextEventId } from '../engine/stats';

interface EventLogState {
  columns: [EventEntry[], EventEntry[], EventEntry[], EventEntry[]];
  addEvent: (column: EventColumn, entry: Omit<EventEntry, 'id' | 'column'>) => void;
  highlightWindow: HighlightWindow | null;
  setHighlightWindow: (hw: HighlightWindow | null) => void;
  clearAll: () => void;
}

const EventLogContext = createContext<EventLogState | null>(null);

export function EventLogProvider({ children }: { children: React.ReactNode }) {
  const [columns, setColumns] = useState<[EventEntry[], EventEntry[], EventEntry[], EventEntry[]]>([[], [], [], []]);
  const [highlightWindow, setHighlightWindowState] = useState<HighlightWindow | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addEvent = useCallback((column: EventColumn, entry: Omit<EventEntry, 'id' | 'column'>) => {
    setColumns(prev => {
      const next = [...prev] as [EventEntry[], EventEntry[], EventEntry[], EventEntry[]];
      const full: EventEntry = { ...entry, id: nextEventId(), column };
      next[column] = [full, ...next[column]];
      if (next[column].length > EVENT_LOG_MAX) next[column] = next[column].slice(0, EVENT_LOG_MAX);
      return next;
    });
  }, []);

  const setHighlightWindow = useCallback((hw: HighlightWindow | null) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setHighlightWindowState(hw);
    if (hw) {
      highlightTimerRef.current = setTimeout(() => setHighlightWindowState(null), 5000);
    }
  }, []);

  const clearAll = useCallback(() => {
    setColumns([[], [], [], []]);
  }, []);

  const value = useMemo(() => ({
    columns, addEvent, highlightWindow, setHighlightWindow, clearAll,
  }), [columns, addEvent, highlightWindow, setHighlightWindow, clearAll]);

  return (
    <EventLogContext.Provider value={value}>
      {children}
    </EventLogContext.Provider>
  );
}

export function useEventLogContext(): EventLogState {
  const ctx = useContext(EventLogContext);
  if (!ctx) throw new Error('useEventLogContext must be inside EventLogProvider');
  return ctx;
}
